import { useTranslation } from 'react-i18next';

import { TableChartOutlined as TableChartOutlinedIcon } from '@mui/icons-material';
import { SvgIcon, ToggleButton, Tooltip } from '@mui/material';

import { ReactComponent as GroupIcon } from '@/assets/icons/GroupWorkRounded.svg';
import { ReactComponent as TimelineCustomIcon } from '@/assets/icons/TimelineCustom.svg';
import { AppButton } from '@/shared/ui/AppButton';

import {
  ButtonGroup,
  GranularityToggle,
  ToolbarDivider,
  ToolbarRoot,
  ToolbarSection,
  getToolbarAppButtonSx,
} from './GeneratikTableToolbar.styles';
import { GeneratikTableToolbarProps } from './GeneratikTableToolbar.types';
import ToolbarColumnsMenu from './ToolbarColumnsMenu';
import ToolbarDropdown from './ToolbarDropdown';
import ToolbarFilterButton from './ToolbarFilterButton';
import ToolbarLeftSection from './ToolbarLeftSection';
import ToolbarRightSection from './ToolbarRightSection';
import ToolbarSearch from './ToolbarSearch';
import ToolbarSortMenu from './ToolbarSortMenu';
import ToolbarViewToggle from './ToolbarViewToggle';
import useToolbarState from './hooks/useToolbarState';

const GeneratikTableToolbar = ({
  leftButtons = [],
  rightButtons = [],
  showSearch = true,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  showColumns = true,
  disableColumnsButton = false,
  disableGroupButton = false,
  columns = [],
  visibleColumns = {},
  campaignColumnVisibility = {},
  onColumnVisibilityChange,
  showSort = true,
  disableSortButton = false,
  sortOptions = [],
  sortActions,
  showGroup = true,
  groupOptions = [],
  currentGroup,
  onGroupChange,
  showFilter = true,
  onFilterClick,
  filterButtonRef,
  showViewToggle = true,
  viewMode = 'grid',
  onViewModeChange,
  isModal = false,
  seamless = false,
  plain = false,
  startContent,
  showVariantToggle = false,
  variant = 'data',
  onVariantChange,
  timelineGranularity = 'week',
  onTimelineGranularityChange,
  effectiveTimelineAxis = 'calendar',
}: GeneratikTableToolbarProps) => {
  const isTimelineVariantActive = variant === 'timeline';
  const { t } = useTranslation();

  const {
    collapseLeft,
    collapseMiddle,
    searchOpen,
    localSearchValue,
    filterAnchor,
    toolbarRef,
    searchInputRef,
    handleColumnToggle,
    handleFilterClick,
    handleFilterClose,
    handleGroupSelect,
    handleSearchClick,
    handleSearchChange,
    handleSearchBlur,
    handleSearchKeyDown,
    groupDropdownOptions,
  } = useToolbarState({
    searchValue,
    visibleColumns,
    sortOptions,
    groupOptions,
    onSearchChange,
    onColumnVisibilityChange,
    onGroupChange,
    onFilterClick,
    leftButtonLabels: Array.isArray(leftButtons) ? leftButtons.map((b) => b.label) : [],
    rightButtonsCount: Array.isArray(rightButtons) ? rightButtons.length : rightButtons ? 1 : 0,
    showSearch,
    showColumns,
    showSort,
    showGroup,
    showFilter,
    showViewToggle,
    showVariantToggle,
  });

  return (
    <ToolbarRoot
      ref={toolbarRef}
      isModal={isModal}
      seamless={seamless}
      plain={plain}
      data-testid="table-actions"
    >
      <ToolbarSection>
        {startContent}
        <ToolbarLeftSection buttons={leftButtons} collapsed={collapseLeft} />
      </ToolbarSection>

      <ToolbarSection>
        {showSearch && (
          <ToolbarSearch
            searchOpen={searchOpen}
            localSearchValue={localSearchValue}
            searchInputRef={searchInputRef}
            placeholder={searchPlaceholder}
            onSearchClick={handleSearchClick}
            onSearchChange={handleSearchChange}
            onSearchBlur={handleSearchBlur}
            onSearchKeyDown={handleSearchKeyDown}
          />
        )}

        <ButtonGroup>
          {showSort && sortOptions.length > 0 && sortActions && (
            <ToolbarDropdown
              label={t('Sort')}
              icon={
                <SvgIcon viewBox="0 0 16 16" sx={{ width: '1rem', height: '1rem' }}>
                  <path
                    d="M3.37301 4C3.37301 3.72386 3.59687 3.5 3.87301 3.5C4.14915 3.50001 4.37301 3.72386 4.37301 4V10.8529L6.16663 9.18815C6.369 9.00026 6.68577 9.01183 6.87366 9.21419C7.06146 9.41656 7.04931 9.73336 6.84697 9.92122L4.21351 12.3665C4.01904 12.5471 3.71707 12.5442 3.52601 12.36L0.988896 9.91471C0.790118 9.72311 0.784351 9.40651 0.975875 9.20768C1.16752 9.00886 1.48409 9.00302 1.68291 9.19466L3.37301 10.8236V4ZM11.4088 10.3379C11.685 10.3379 11.9088 10.5618 11.9088 10.8379C11.9088 11.114 11.685 11.3379 11.4088 11.3379H8.9635C8.68738 11.3379 8.4635 11.114 8.4635 10.8379C8.46352 10.5618 8.68739 10.3379 8.9635 10.3379H11.4088ZM13.039 7.12826C13.3152 7.12826 13.539 7.35211 13.539 7.62826C13.5389 7.90431 13.3151 8.12826 13.039 8.12826H8.9635C8.68745 8.12823 8.46361 7.9043 8.4635 7.62826C8.4635 7.35213 8.68738 7.12828 8.9635 7.12826H13.039ZM14.6692 3.91797C14.9453 3.91797 15.1691 4.14194 15.1692 4.41797C15.1692 4.69411 14.9454 4.91797 14.6692 4.91797H8.9635C8.68738 4.91795 8.4635 4.6941 8.4635 4.41797C8.46364 4.14195 8.68746 3.91799 8.9635 3.91797H14.6692Z"
                    fill="currentColor"
                  />
                </SvgIcon>
              }
              collapsed={collapseMiddle}
              disabled={disableSortButton}
              data-testid="sort-dropdown"
              customContent={(close) => (
                <ToolbarSortMenu
                  options={sortOptions}
                  sortActions={sortActions}
                  campaignColumnVisibility={campaignColumnVisibility}
                  onDisabledItemClick={handleFilterClick}
                  onClose={close}
                />
              )}
            />
          )}

          {showFilter && (
            <ToolbarFilterButton
              ref={filterButtonRef}
              collapsed={collapseMiddle}
              filterAnchor={filterAnchor}
              hasCustomFilterClick={Boolean(onFilterClick)}
              onFilterClick={handleFilterClick}
              onFilterClose={handleFilterClose}
            />
          )}

          {showGroup && groupOptions.length > 0 && (
            <ToolbarDropdown
              label={
                currentGroup
                  ? (groupOptions.find((o) => o.field === currentGroup)?.label ?? t('Group'))
                  : t('Group')
              }
              icon={<GroupIcon />}
              options={groupDropdownOptions}
              selectedId={currentGroup}
              onSelect={handleGroupSelect}
              collapsed={collapseMiddle}
              showNoneOption
              noneLabel={t('None')}
              campaignColumnVisibility={campaignColumnVisibility}
              onDisabledItemClick={handleFilterClick}
              disabled={disableGroupButton}
              data-testid="group-dropdown"
            />
          )}
        </ButtonGroup>

        {((showSort && sortOptions.length > 0) ||
          showFilter ||
          (showGroup && groupOptions.length > 0)) &&
          showColumns && <ToolbarDivider />}

        {showColumns && (
          <ToolbarColumnsMenu
            columns={columns.filter((c) => c.field !== 'actions')}
            visibleColumns={visibleColumns}
            campaignColumnVisibility={campaignColumnVisibility}
            onColumnToggle={handleColumnToggle}
            onDisabledItemClick={handleFilterClick}
            collapsed={collapseMiddle}
            disabled={disableColumnsButton}
          />
        )}

        {showViewToggle && (
          <ToolbarViewToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            disabled={isTimelineVariantActive}
          />
        )}

        {showVariantToggle && isTimelineVariantActive && effectiveTimelineAxis !== 'bookings' && (
          <GranularityToggle
            color="primary"
            size="small"
            exclusive
            value={timelineGranularity}
            onChange={(_, value) =>
              value && onTimelineGranularityChange?.(value as 'week' | 'month')
            }
            data-testid="timeline-granularity-toggle"
          >
            <ToggleButton data-testid="timeline-granularity-week" value="week">
              {t('week')}
            </ToggleButton>
            <ToggleButton data-testid="timeline-granularity-month" value="month">
              {t('month')}
            </ToggleButton>
          </GranularityToggle>
        )}

        {showVariantToggle && (
          <Tooltip
            title={
              collapseLeft
                ? isTimelineVariantActive
                  ? t('Switch to Table')
                  : t('Switch to Timeline')
                : ''
            }
            arrow
            disableHoverListener={!collapseLeft}
          >
            <AppButton
              variant="primary"
              size="small"
              collapsed={collapseLeft}
              onClick={() => onVariantChange?.(isTimelineVariantActive ? 'data' : 'timeline')}
              data-testid="timeline-variant-toggle"
              sx={getToolbarAppButtonSx(collapseLeft)}
              startIcon={
                isTimelineVariantActive ? (
                  <TableChartOutlinedIcon />
                ) : (
                  <SvgIcon component={TimelineCustomIcon} inheritViewBox />
                )
              }
            >
              {isTimelineVariantActive ? t('Switch to Table') : t('Switch to Timeline')}
            </AppButton>
          </Tooltip>
        )}

        {(Array.isArray(rightButtons) ? rightButtons.length > 0 : !!rightButtons) &&
          (showFilter || showViewToggle || showVariantToggle) && <ToolbarDivider />}

        <ToolbarRightSection buttons={rightButtons} collapsed={collapseLeft} />
      </ToolbarSection>
    </ToolbarRoot>
  );
};

export default GeneratikTableToolbar;
